import { Injectable, Injector } from '@angular/core';
import { Router, ActivatedRoute, NavigationExtras } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Subject, take } from 'rxjs';

import { NodeChildAssociationEntry, NodeEntry } from '@alfresco/js-api';
import { ContentApiService } from '@alfresco/aca-shared';
import { ContentManagementService } from '@adw/projects/aca-content/src/lib/services/content-management.service';

import { WkShareDocumentComponent } from '../components/wk-share-document/wk-share-document.component';
import { UploadVersionDialogComponent } from '../dialogs/upload-version/upload-version.dialog';
import { CommonConstants } from '../shared';
import { NodeInfoService } from './node.service';
import { RenditionService } from '@alfresco/adf-content-services';
import { WkSettingsService } from './wk-settings.service';
import { CancelWorkflowService } from '../components/shared/services/cancel-workflow.service';
import { NodeActionsFacade } from './node-actions.facade';
import { Store } from '@ngrx/store';

import { ValidationLogsDownloadService } from './validation-logs-download.service';

import { AppStore } from '../store/states/app.state';
import { getAppSelection } from '../store/selectors/app.custom-selectors';

import { getDocumentValidationStatus } from '../modules/wk-document-decorator/state/selectors/get.documentvalidationstatus';

@Injectable({
  providedIn: 'root',
})
export class CustomContentManagementService {
  nodesCheckouted = new Subject<void>();
  linksUnshared = new Subject<any>();
  versionHistory$ = new Subject<boolean>();
  nodesDeleted = new Subject<any>();
  processStarted = new Subject<any>();
  private previewLocation: string | null = null;
  currentUrl: string;
  private readonly customRouter: Router;
  private readonly route: ActivatedRoute;
  private readonly store: Store<AppStore>;
  contentService: ContentManagementService;
  nodeInfoservice: NodeInfoService;
  isNavigating: any;

  constructor(
    public injector: Injector,
    private readonly dialogRef: MatDialog,
    private readonly contentApi: ContentApiService,
    private readonly renditionService: RenditionService,
    private readonly wkAppSettingsService: WkSettingsService,
    private readonly cancelWorkflowService: CancelWorkflowService,
    private readonly validationLogsDownloadService: ValidationLogsDownloadService
  ) {
    this.customRouter = injector.get<Router>(Router);
    this.route = injector.get<ActivatedRoute>(ActivatedRoute);
    this.route?.queryParams?.subscribe?.((params) => {
      this.previewLocation = params['previewLocation'];
    });
    this.contentService = injector.get<ContentManagementService>(
      ContentManagementService
    );
    this.nodeInfoservice = injector.get<NodeInfoService>(NodeInfoService);
    this.store = injector.get<Store<AppStore>>(Store);
  }

  checkoutNode(): void {
    this.nodesCheckouted.next();
  }

  public assignWorkflowStep(node: NodeChildAssociationEntry) {
    const taskStepId = node?.entry?.properties?.[CommonConstants.stepId]?.[0];
    if (!taskStepId) {
      return;
    }

    // Get current route path without query or hash
    const currentUrl = this.customRouter.url;
    const baseUrl = currentUrl.split('?')[0].split('#')[0];

    // Split the base URL into proper path segments
    const pathSegments = baseUrl
      .split('/')
      .filter((segment) => segment.length > 0);
    const targetPathSegments = [...pathSegments, 'task-detail', taskStepId];

    const navigationExtras: NavigationExtras = {
      queryParams: { previewLocation: baseUrl },
      // Force navigation even if URL is the same
      onSameUrlNavigation: 'reload',
    };

    // Prevent multiple navigations
    if (this.isNavigating) {
      return;
    }
    this.isNavigating = true;

    // Navigate using path segments to avoid encoding issues
    this.customRouter
      .navigate(targetPathSegments, navigationExtras)
      .then((navigationResult: boolean) => {
        if (!navigationResult) {
          console.warn('Navigation to task detail failed');
        }
      })
      .catch((error) => {
        console.error('Navigation error:', error);
      })
      .finally(() => {
        this.isNavigating = false;
      });
  }

  public cancelWorkflow(node: NodeChildAssociationEntry) {
    this.currentUrl = this.customRouter.url;
    const {
      entry: { id, name, properties },
    } = node || { entry: {} };
    if (!id) {
      return;
    }
    const taskStepId = properties[CommonConstants.stepId][0];
    if (!taskStepId) {
      return;
    }
    const rawProcessId = properties['apollo:processId'];
    const processId = rawProcessId ? String(rawProcessId).trim() : '';
    if (!processId) {
      return;
    }
    const attachments = [{ name, sourceId: `${id};` }];
    this.cancelWorkflowService.openCancelWorkflowDialog(
      {
        data: {
          id: processId,
          attachments,
          processTemplateName: 'cancelWorkflow',
        },
      },
      node
    );
  }

  public downloadValidationList(): void {
    this.store
      .select(getAppSelection)
      .pipe(take(1))
      .subscribe(({ file }) => {
        if (file?.entry?.name) {
          this.store
            .select(getDocumentValidationStatus)
            .pipe(take(1))
            .subscribe((data) => {
              const errorOnlyResults =
                this.validationLogsDownloadService.getErrorEntries(
                  data.validationResults
                );
              this.validationLogsDownloadService.downloadValidationLogs(
                errorOnlyResults,
                file.entry.name
              );
            });
        }
      });
  }

  shareNode(node: NodeChildAssociationEntry): void {
    if (node && node.entry) {
      // shared and favorite
      const id = node.entry.id;
      if (id) {
        this.contentApi.getNodeInfo(id).subscribe((entry) => {
          this.openShareLinkDialog({ entry });
        });
      }
    }
  }

  public openShareLinkDialog(node: NodeEntry): void {
    this.dialogRef
      .open(WkShareDocumentComponent, {
        restoreFocus: true,
        width: '600px',
        panelClass: 'adf-share-link-dialog',
        data: {
          node,
          baseShareUrl: this.wkAppSettingsService.baseShareUrl,
        },
      })
      .afterClosed()
      .subscribe((deletedSharedLink) => {
        const nodeFacade = this.injector.get(NodeActionsFacade);
        nodeFacade.dispatchSetSelectedNodes([deletedSharedLink || node]);
        if (deletedSharedLink) {
          this.linksUnshared.next(deletedSharedLink);
        }
      });
  }

  printFile(node: any) {
    if (node?.entry) {
      // shared and favorite
      const id = node.entry.nodeId || node.entry.guid || node.entry.id;
      const mimeType = node.entry.content.mimeType;

      if (id) {
        this.renditionService.printFileGeneric(id, mimeType);
      }
    }
  }

  versionUpdateDialog(node, file) {
    return this.dialogRef.open(UploadVersionDialogComponent, {
      data: { node, file, isTypeList: false },
      panelClass: 'adf-version-manager-dialog-panel-upload',
      width: '600px',
    });
  }

  navigatePreviewMode(node, nodeId: string) {
    this.currentUrl = this.customRouter.url;
    let previewLocation = this.previewLocation;
    if (!this.previewLocation) {
      const previewLocationMatch = this.currentUrl.match(
        /(?<=previewLocation=)[^&#]+/
      );
      if (previewLocationMatch) {
        previewLocation = previewLocationMatch[0];
      }
    }
    if (this.currentUrl.includes('previewLocation=libraries')) {
      this.customRouter.navigateByUrl(
        `${previewLocation}/${node.parentId}/preview/${nodeId}`
      );
    } else if (
      this.currentUrl.includes('/libraries/') &&
      this.currentUrl.includes('/preview/') &&
      !this.currentUrl.includes('/favorite/')
    ) {
      previewLocation = this.customRouter.url
        .substring(0, this.customRouter.url.indexOf('/', 1))
        .replace(/\//g, '');
      this.customRouter.navigateByUrl(
        `${previewLocation}/${node.parentId}/preview/${nodeId}`
      );
    } else if (this.currentUrl.includes('previewLocation=dashboard')) {
      this.customRouter.navigateByUrl(`dashboard/preview/${nodeId}`);
    } else if (this.currentUrl.includes('/dashboard/')) {
      this.customRouter.navigateByUrl(`dashboard/preview/${nodeId}`);
    } else if (
      this.currentUrl.includes('/search;q=') &&
      this.currentUrl.includes('/preview')
    ) {
      this.getSearchString('/', '=', nodeId);
    } else if (this.currentUrl.includes('/all-docs-dashboard/')) {
      this.customRouter.navigateByUrl(`all-docs-dashboard/preview/${nodeId}`);
    } else if (this.currentUrl.includes('previewLocation=search')) {
      this.getSearchString('&', '%3D', nodeId);
    } else if (this.currentUrl.includes('/favorite/libraries/')) {
      this.customRouter.navigateByUrl(
        `favorite/libraries/${node.parentId}/preview/${nodeId}`
      );
    } else if (this.currentUrl.includes('previewLocation=favorite')) {
      this.customRouter.navigateByUrl(
        `${previewLocation}/libraries/${node.parentId}/preview/${nodeId}`
      );
    } else {
      this._navigatePreviewMode(nodeId);
    }
  }

  private _navigatePreviewMode(nodeId: string) {
    if (
      this.currentUrl.includes(`previewLocation=task-list`) ||
      this.currentUrl.includes('/task-list/')
    ) {
      this.customRouter.navigateByUrl(`task-list/preview/${nodeId}`);
    } else if (
      this.currentUrl.includes(`previewLocation=expanded-content`) ||
      this.currentUrl.includes('/expanded-content/')
    ) {
      this.customRouter.navigateByUrl(`expanded-content/preview/${nodeId}`);
    } else if (
      this.currentUrl.includes(`previewLocation=process-list`) ||
      this.currentUrl.includes('/process-list/')
    ) {
      this.customRouter.navigateByUrl(`process-list/preview/${nodeId}`);
    } else if (
      // this case is for return to ADD
      this.currentUrl.includes(`previewLocation=all-docs-dashboard`)
    ) {
      this.customRouter.navigateByUrl(`/all-docs-dashboard`);
    }
  }

  getSearchString(str, spliStr, nodeId) {
    const subString = this.customRouter.url
      .substring(0, this.customRouter.url.indexOf(str, 1))
      .replace(/\//g, '');
    const splitText = subString.split(spliStr);
    const searchText = splitText[1];
    this.customRouter.navigateByUrl(
      `/search;q=${searchText}/preview/${nodeId}`
    );
  }

  async checkOutNodeForEdition(nodeId: string): Promise<string> {
    return this.nodeInfoservice.checkOut(nodeId).then((data: any) => {
      const nodeFacade = this.injector.get(NodeActionsFacade);
      nodeFacade.dispatchSnackbarInfo('APP.INFO_DRAWER.CHECK_OUT.SUCCESS');
      return data.ids[0];
    });
  }

  navigateToEditComponent(
    currentLocation: string,
    folderId: string,
    nodeId: string,
    workingCopyId?: string
  ) {
    const navigationExtras: NavigationExtras = {
      queryParams: {
        previewLocation: currentLocation,
        folderId,
        nodeId: workingCopyId ? workingCopyId : nodeId,
      },
    };
    this.customRouter.navigate(['edit'], navigationExtras);
  }
}
